// Workspace overview query hook (overview.rs).
//   GET /v1/workspaces/{workspaceId}/overview?from=&to=
//   (from/to are RFC3339 instants, both optional — `from` absent is all time
//   (from the workspace's first response or creation), `to` absent is now).
//
// The page asks for a length ("the last 24 hours"), not an instant: the key
// carries the length and `from` is computed each time the request is made,
// so a tab left open (focus refetches) keeps meaning the last 24 hours
// rather than drifting into "since the tab was opened".

import { parseDuration } from "@pej/kore/components";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { http } from "./http";
import { overviewKeys } from "./keys";
import type { WorkspaceOverview } from "./types";

export interface OverviewWindow {
    /** A duration ("24h", "7d"): the window is the last that long. Absent is all time. */
    within?: string;
}

function buildQuery({ within }: OverviewWindow): string {
    if (!within) return "";
    const from = new Date(Date.now() - (parseDuration(within) ?? 0)).toISOString();
    return `?${new URLSearchParams({ from })}`;
}

export function useOverview(workspaceId: string, window: OverviewWindow = {}) {
    return useQuery({
        queryKey: overviewKeys.detail(workspaceId, window),
        queryFn: () => http.get<WorkspaceOverview>(`/v1/workspaces/${workspaceId}/overview${buildQuery(window)}`),
        enabled: Boolean(workspaceId),
        // A range change is a new query key; hold the previous window's data
        // on screen until the new one resolves (same idiom as useStats).
        placeholderData: keepPreviousData,
    });
}
